import { Maybe, Milestone, Rule, Ruleset } from 'types/dt-graphql-apollo-v2';
import { getSchemaTypeForRule } from 'utils/data-validation';

export enum StepName {
  'COLLECTION' = 0,
  'RULESET' = 1,
  'RULE' = 2,
}

/** Type that will hold information like this:
 * ['rulesetId']: ['ruleId1', 'ruleId2'] */
type RulesetPlaceholderType = Record<string, string[]>;

/** Type that will hold information like this:
 * ['milestoneId']: RulesetPlaceholderType */
export type CopyRulesPlaceholderType = Record<string, RulesetPlaceholderType>;

export const independentRulesId = 'only-independent-rules';
export const independentRulesName = 'Independent rules';

export const checkIfCollectionIsSelected = (
  milestoneId: string,
  copyPlaceholder: CopyRulesPlaceholderType
) => Object.keys(copyPlaceholder).includes(milestoneId);

export const checkIfRulesetIsSelected = (
  copyPlaceholder: CopyRulesPlaceholderType,
  rulesetId: string,
  milestoneId?: string
) => {
  if (!milestoneId || !rulesetId || !copyPlaceholder[milestoneId]) return false;

  return Object.keys(copyPlaceholder[milestoneId]).includes(rulesetId);
};

export const checkIfRuleIsSelected = (
  copyPlaceholder: CopyRulesPlaceholderType,
  milestoneId?: string,
  rulesetId?: string,
  ruleId?: string
) => {
  if (
    !milestoneId ||
    !rulesetId ||
    !ruleId ||
    !copyPlaceholder[milestoneId] ||
    !copyPlaceholder[milestoneId][rulesetId]
  )
    return false;

  return Object.values(copyPlaceholder[milestoneId][rulesetId]).includes(
    ruleId
  );
};

/** Populates each milestone's ruleset with related rules. */
export const getMilestonesWithRulesetRules = (milestones: Milestone[]) =>
  milestones.map((milestone) => ({
    ...milestone,
    rulesets: {
      items: milestone.rulesets?.items.map((ruleset) => ({
        ...ruleset,
        rules: {
          items:
            milestone.rules?.items.filter(
              (rule) =>
                rule?.rulesets?.items[0]?.externalId === ruleset?.externalId
            ) ?? [],
        },
      })),
    },
  })) as Milestone[];

export const getAllMilestonesRules = (milestones: Milestone[]) =>
  (milestones.flatMap((m) => m.rules?.items) as Rule[]) ?? [];

export const getAllMilestonesRulesets = (milestones: Milestone[]) =>
  (milestones.flatMap((m) => m.rulesets?.items) as Ruleset[]) ?? [];

export const getRulesCountFromMilestone = (milestone: Milestone) =>
  milestone.rules?.items.length || 0;

export const getRulesetsCountFromMilestone = (milestone: Milestone) =>
  milestone.rulesets?.items.length || 0;

export const getRulesCountFromRuleset = (ruleset: Ruleset) =>
  ruleset.rules?.items.length || 0;

/** Get the number of selected rulesets and rules from the copyPlaceholder. */
export const getCountsFromCopyPlaceholder = (
  copyPlaceholder: CopyRulesPlaceholderType
) => {
  let rulesetsCount = 0;
  let rulesCount = 0;

  // For each milestone, add the count of the rulesets
  Object.keys(copyPlaceholder).forEach((milestoneId) => {
    const rulesets = copyPlaceholder[milestoneId];
    rulesetsCount += Object.keys(rulesets).length;

    // Remove the count of the "ghost" independent ruleset
    if (Object.keys(rulesets).includes(independentRulesId)) {
      rulesetsCount -= 1;
    }

    // Add the count of the rules for each ruleset
    Object.values(rulesets).forEach((ruleIdsList) => {
      rulesCount += ruleIdsList.length || 0;
    });
  });
  return { rulesetsCount, rulesCount };
};

/** Add a milestone and its rulesets + rules into the copyPlaceholder. */
export const addMilestoneToCopyPlaceholder = (
  milestone: Milestone,
  copyPlaceholder: CopyRulesPlaceholderType,
  destinationRuleset?: Ruleset | null
) => {
  /* When a milestone is pressed and selected, we also want to select all the rulesets and rules
  found inside this milestone. */
  const rulesetState: RulesetPlaceholderType = {};
  milestone.rulesets?.items.forEach((ruleset) => {
    const canSelectRuleset = checkIfItemCanBeSelected(
      ruleset as Ruleset,
      StepName.RULESET,
      destinationRuleset
    );
    if (ruleset && canSelectRuleset) {
      rulesetState[ruleset.externalId] =
        ruleset.rules?.items.map((rule) => rule?.externalId || '') || [];
    }
  });
  const independentRulesFromMilestone = milestone?.rules?.items.filter(
    (r) => !r?.rulesets?.items.length
  );

  // Add all the independent rules into a 'ghost' ruleset
  rulesetState[independentRulesId] = [];
  independentRulesFromMilestone?.forEach((rule) => {
    const canSelectRule = checkIfItemCanBeSelected(
      rule as Rule,
      StepName.RULE,
      destinationRuleset
    );

    if (rule && canSelectRule)
      rulesetState[independentRulesId].push(rule.externalId);
  });

  return {
    ...copyPlaceholder,
    [milestone.externalId]: rulesetState,
  };
};

/** Add a list of ruleset and their rules into the copyPlaceholder, inside the specified milestone. */
export const addRulesetsToCopyPlaceholder = (
  rulesets: Maybe<Ruleset>[],
  milestoneId: string,
  copyPlaceholder: CopyRulesPlaceholderType,
  destinationRuleset?: Ruleset | null
) => {
  const currentRulesetState = copyPlaceholder[milestoneId] ?? {};

  rulesets.forEach((ruleset) => {
    if (!ruleset) return;

    const canSelectRuleset = checkIfItemCanBeSelected(
      ruleset as Ruleset,
      StepName.RULESET,
      destinationRuleset
    );

    if (canSelectRuleset) {
      if (!currentRulesetState[ruleset.externalId]) {
        // First, add the ruleset as empty
        currentRulesetState[ruleset.externalId] = [];
      }
      // Then, add all the rules linked to this ruleset
      ruleset.rules?.items.forEach((rule) => {
        const canSelectRule = checkIfItemCanBeSelected(
          rule as Rule,
          StepName.RULE,
          destinationRuleset
        );

        if (rule && canSelectRule)
          currentRulesetState[ruleset.externalId].push(rule.externalId);
      });
    }
  });

  return {
    ...copyPlaceholder,
    [milestoneId]: currentRulesetState,
  };
};

/** Add a list of rules into the copyPlaceholder, inside the specified ruleset and milestone. */
export const addRulesToCopyPlaceholder = (
  rules: Maybe<Rule>[],
  milestoneId: string,
  rulesetId: string,
  copyPlaceholder: CopyRulesPlaceholderType,
  destinationRuleset?: Ruleset | null
) => {
  const currentRulesetState = copyPlaceholder[milestoneId] ?? {};

  if (!currentRulesetState[rulesetId]) {
    currentRulesetState[rulesetId] = [];
  }

  // Add the rules over the existing state
  rules.forEach((rule) => {
    const canSelectRule = checkIfItemCanBeSelected(
      rule as Rule,
      StepName.RULE,
      destinationRuleset
    );

    if (rule && canSelectRule) {
      currentRulesetState[rulesetId].push(rule.externalId);
    }
  });

  return {
    ...copyPlaceholder,
    [milestoneId]: currentRulesetState,
  };
};

/** From a list of rulesets, remove all the rulesets that are already added into the copyPlaceholder. */
export const filterSelectedRulesets = (
  rulesets: Maybe<Ruleset>[],
  milestoneId: string,
  copyPlaceholder: CopyRulesPlaceholderType
) =>
  rulesets?.filter(
    (ruleset) =>
      ruleset &&
      !checkIfRulesetIsSelected(
        copyPlaceholder,
        ruleset.externalId,
        milestoneId
      )
  ) || [];

/** Check if the shema type of the ruleset matches the destination ruleset schema type. */
const checkIfRulesetMatchesDestination = (
  ruleset: Maybe<Ruleset>,
  destinationRuleset: Ruleset | null
) => {
  if (!ruleset) return false;
  if (ruleset.externalId === independentRulesId) return true;

  return (
    ruleset.schemaType === destinationRuleset?.schemaType &&
    getRulesCountFromRuleset(ruleset) > 0
  );
};

/** Check if the shema type of the rule matches the destination ruleset schema type. */
const checkIfRuleMatchesDestination = (
  rule: Maybe<Rule>,
  destinationRuleset: Ruleset | null
) => {
  if (!rule) return false;

  return getSchemaTypeForRule(rule) === destinationRuleset?.schemaType;
};

/** This function receives an item which is either a Milestone / Ruleset / Rule and checks whatever an item is disabled or not
 * based on the content found inside the item.
 *
 * In the current case, only a Milestone or a Ruleset can be disabled. Empty rulesets can only be copied into a milestone,
 * but if we want to copy into a ruleset, then we can only copy from rulesets with existing rules. */
const checkIfItemIsDisabledByContent = (
  item: Milestone | Ruleset | Rule,
  stepName: StepName,
  destinationRuleset?: Ruleset | null
) => {
  if (!item) return false;

  switch (stepName) {
    case StepName.COLLECTION:
      return (
        getRulesCountFromMilestone(item as Milestone) === 0 &&
        getRulesetsCountFromMilestone(item as Milestone) === 0
      );
    case StepName.RULESET:
      return destinationRuleset
        ? getRulesCountFromRuleset(item as Ruleset) === 0
        : false;
    default:
      return false;
  }
};

/** This function receives an item which is either a Milestone / Ruleset / Rule and checks whatever an item is disabled or not
 * based on the logic of item. The logic is determined by the scham type found in an item.
 *
 * In the current case, only a Ruleset or a Rule can be disabled. A ruleset or rule is disabled if its schema type is differnet from
 * the schema type found inside the *destinationRuleset*. */
const checkIfItemIsDisabledByLogic = (
  item: Milestone | Ruleset | Rule,
  stepName: StepName,
  destinationRuleset?: Ruleset | null
) => {
  if (!item || !destinationRuleset) return false;

  switch (stepName) {
    case StepName.RULESET:
      return !checkIfRulesetMatchesDestination(
        item as Ruleset,
        destinationRuleset
      );
    case StepName.RULE:
      return !checkIfRuleMatchesDestination(item as Rule, destinationRuleset);
    default:
      return false;
  }
};

/** This function receives an item which is either a Milestone / Ruleset  and checks whatever an item is disabled or not
 * based on the status of all the children.
 *
 * In the current case, only a Milestone or a Ruleset can be disabled. A milestone or ruleset can be disabled if all
 * the children inside are disabled as well. */
const checkIfItemIsDisabledByChildren = (
  item: Milestone | Ruleset | Rule,
  stepName: StepName,
  destinationRuleset?: Ruleset | null
) => {
  if (!item || !destinationRuleset) return false;

  switch (stepName) {
    case StepName.COLLECTION:
      return checkIfCollectionChildrenAreDisabled(
        item as Milestone,
        destinationRuleset
      );
    case StepName.RULESET:
      return checkIfRulesetChildrenAreDisabled(
        item as Ruleset,
        destinationRuleset
      );
    default:
      return false;
  }
};

export const checkIfItemIsDisabled = (
  item: Milestone | Ruleset | Rule,
  stepName: StepName,
  destinationRuleset?: Ruleset | null
) =>
  checkIfItemIsDisabledByLogic(item, stepName, destinationRuleset) ||
  checkIfItemIsDisabledByContent(item, stepName, destinationRuleset) ||
  checkIfItemIsDisabledByChildren(item, stepName, destinationRuleset);

const checkIfItemCanBeSelected = (
  item: Milestone | Ruleset | Rule,
  stepName: StepName,
  destinationRuleset?: Ruleset | null
) => {
  if (destinationRuleset) {
    return !checkIfItemIsDisabled(item, stepName, destinationRuleset);
  }
  return true;
};

/** Checks if a collection has all its children disabled.
 *
 * A collection can be disabled only if *destinationRuleset* is valid. The disabled state will be determined
 * by the children rulesets and rules. If all the children are disabled, then the parent collection should be disabled as well. */
const checkIfCollectionChildrenAreDisabled = (
  milestone: Milestone,
  destinationRuleset: Ruleset
): boolean => {
  const allRulesetsAreDisabled =
    milestone.rulesets?.items.every((ruleset) =>
      checkIfItemIsDisabled(
        ruleset as Ruleset,
        StepName.RULESET,
        destinationRuleset
      )
    ) || false;

  const allRulesAreDisabled =
    milestone.rules?.items.every((rule) =>
      checkIfItemIsDisabled(rule as Rule, StepName.RULE, destinationRuleset)
    ) || false;

  return allRulesetsAreDisabled && allRulesAreDisabled;
};

/** Checks if a ruleset has all its children disabled.
 *
 * A ruleset can be disabled by its children only if *destinationRuleset* is valid.
 * The disabled state will be determined by the children rules found inside the ruleset.
 * If all the rules are disabled, then the parent ruleset should be disabled as well. */
const checkIfRulesetChildrenAreDisabled = (
  ruleset: Ruleset,
  destinationRuleset: Ruleset
): boolean => {
  const isDisabled =
    ruleset.rules?.items.every((rule) =>
      checkIfItemIsDisabled(rule as Rule, StepName.RULE, destinationRuleset)
    ) || false;

  return isDisabled;
};

export const getDisabledTooltipMessage = (
  item: Milestone | Ruleset | Rule,
  stepName: StepName,
  destinationRuleset?: Ruleset | null
): string => {
  if (checkIfItemIsDisabledByContent(item, stepName, destinationRuleset)) {
    return 'Empty item, no content to copy.';
  }
  if (checkIfItemIsDisabledByChildren(item, stepName, destinationRuleset)) {
    return 'The children do not match the schema type of the destination.';
  }
  if (checkIfItemIsDisabledByLogic(item, stepName, destinationRuleset)) {
    return 'Item is disabled by the mismatch of schema types.';
  }

  return 'Item is disabled.';
};
